import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import '@spotify-internal/react-dates/initialize';
import '@spotify-internal/react-dates/lib/css/_datepicker.css';

import moment from 'moment';
import styled from 'styled-components';
import {
  ButtonPrimary,
  ButtonTertiary,
  cssSpacing,
  DialogAlert,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import {
  DateRangePicker,
  FocusedInputShape,
  isInclusivelyAfterDay,
  isInclusivelyBeforeDay,
} from '@spotify-internal/react-dates';
import { routeExposedLocales } from '@spotify-internal/s4mp-shared';
import useOnClickOutside from '@components/Catalog/ContentArea/CatalogExports/helpers/useOnClickOutside';
import {
  getLanguageAsMomentLocale,
  getLanguagePreferenceAsMomentLocale,
} from '@helpers/translate';

interface DateRange {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
}

// Dynamically import moment locales based on the exposedLocales from S4MP
for (const language of routeExposedLocales) {
  const momentLang = getLanguageAsMomentLocale(language);
  if (momentLang !== '') {
    require(`moment/locale/${momentLang}`);
  }
}

// Can not style the DatePickerRange itself - see https://github.com/airbnb/react-dates/issues/1030
const StyledDatePicker = styled.div`
  position: absolute;
`;

const StyledDialogAlert = styled(DialogAlert)`
  padding: ${cssSpacing('tighter-4', '8px')} 0;
  width: 660px;
  right: 2em;
  margin-top: ${cssSpacing('tighter-4', '8px')};
  position: absolute;
  border-radius: 4px;
`;

const StyledFooter = styled.div`
  padding: 0 ${cssSpacing('tighter-3', '12px')} ${cssSpacing('tighter-5', '4px')};
  margin-top: 420px;
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const CancelButton = styled(ButtonTertiary)`
  padding: 0 ${cssSpacing('tighter-2', '16px')};
  margin: 0 !important;
`;

const FIRST_ATLAS_DELIVERY = moment('20200928', 'YYYYMMDD').startOf('day');

const useFocusRestrict = (firstBtn: HTMLElement, lastBtn: HTMLElement) => {
  const keyDownHandler = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;

    if (
      !e.shiftKey &&
      (document.activeElement === lastBtn ||
        lastBtn?.contains(e.composedPath()[0] as Node))
    ) {
      firstBtn?.focus();
      e.preventDefault();
    }
    if (
      e.shiftKey &&
      (document.activeElement === firstBtn ||
        firstBtn?.contains(e.composedPath()[0] as Node))
    ) {
      lastBtn?.focus();
      e.preventDefault();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', keyDownHandler);
    if (firstBtn) firstBtn.addEventListener('keydown', keyDownHandler);

    return () => {
      window.removeEventListener('keydown', keyDownHandler);
      if (firstBtn) firstBtn.removeEventListener('keydown', keyDownHandler);
    };
  });
};

const useOnEscape = (
  wrapperRef: RefObject<HTMLDivElement>,
  toggle = () => {},
) => {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (event.key === 'Escape' && wrapperRef.current) {
        toggle();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [wrapperRef, toggle]);
};

interface DateRangePickerWrapperProps {
  createdAtRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
  onCancel: () => void;
}

export function DateRangePickerWrapper(props: DateRangePickerWrapperProps) {
  const t = useT();
  const datePickerPhrases = {
    clearDate: t(
      'atlas.datePicker.clearDate',
      `Clear Date`,
      'Clear a date selection in the date range picker.',
    ),
    clearDates: t(
      'atlas.datePicker.clearDates',
      `Clear Dates`,
      'Clear multiple date selections in the date range picker.',
    ),
    jumpToPrevMonth: t(
      'atlas.datePicker.jumpToPrevMonth',
      `Move backward to switch to the previous month.`,
      'Switch the date range picker to display the previous month.',
    ),
    jumpToNextMonth: t(
      'atlas.datePicker.jumpToNextMonth',
      `Move forward to switch to the next month.`,
      'Switch the date range picker to display the next month.',
    ),
    keyboardShortcuts: t(
      'atlas.datePicker.keyboardShortcuts',
      `Keyboard Shortcuts`,
      'Title for the keyboard shortcuts panel.',
    ),
    showKeyboardShortcutsPanel: t(
      'atlas.datePicker.showKeyboardShortcutsPanel',
      `Open the keyboard shortcuts panel.`,
      'Opens the keyboard shortcuts panel in the date range picker.',
    ),
    hideKeyboardShortcutsPanel: t(
      'atlas.datePicker.hideKeyboardShortcutsPanel',
      `Close the shortcuts panel.`,
      'Closes the keyboard shortcuts panel in the date range picker.',
    ),
    openThisPanel: t(
      'atlas.datePicker.openThisPanel',
      `Open this panel.`,
      'Opens the panel describing keyboard shortcuts for the date range picker.',
    ),
    enterKey: t(
      'atlas.datePicker.enterKey',
      `Enter key`,
      'Enter key on a keyboard to describe a keyboard shortcut.',
    ),
    leftArrowRightArrow: t(
      'atlas.datePicker.leftArrowRightArrow',
      `Right and left arrow keys`,
      'Left and right arrow keys on a keyboard to describe a keyboard shortcut.',
    ),
    upArrowDownArrow: t(
      'atlas.datePicker.upArrowDownArrow',
      `up and down arrow keys`,
      'Up and down arrow keys on a keyboard to describe a keyboard shortcut.',
    ),
    pageUpPageDown: t(
      'atlas.datePicker.pageUpPageDown',
      `page up and page down keys`,
      'Page up and page down arrow keys on a keyboard to describe a keyboard shortcut.',
    ),
    homeEnd: t(
      'atlas.datePicker.homeEnd',
      `Home and end keys`,
      'Home and end keys on a keyboard to describe a keyboard shortcut.',
    ),
    escape: t(
      'atlas.datePicker.escape',
      `Escape key`,
      'Escape key on a keyboard to describe a keyboard shortcut.',
    ),
    questionMark: t(
      'atlas.datePicker.questionMark',
      `Question mark`,
      'Question mark key on a keyboard to describe a keyboard shortcut.',
    ),
    selectFocusedDate: t(
      'atlas.datePicker.selectFocusedDate',
      `Select the date in focus.`,
      'Select a date in the date range picker.',
    ),
    moveFocusByOneDay: t(
      'atlas.datePicker.moveFocusByOneDay',
      `Move backward (left) and forward (right) by one day.`,
      'Move selected date in the date range picker by one day.',
    ),
    moveFocusByOneWeek: t(
      'atlas.datePicker.moveFocusByOneWeek',
      `Move backward (up) and forward (down) by one week.`,
      'Move selected date in the date range picker by one week.',
    ),
    moveFocusByOneMonth: t(
      'atlas.datePicker.moveFocusByOneMonth',
      `Switch months.`,
      'Move selected date in the date range picker by one month.',
    ),
    moveFocustoStartAndEndOfWeek: t(
      'atlas.datePicker.moveFocustoStartAndEndOfWeek',
      `Go to the first or last day of a week.`,
      'Select the first or last day of a week in the date range picker.',
    ),
    returnFocusToInput: t(
      'atlas.datePicker.returnFocusToInput',
      `Return to the date input field.`,
      'Closes the keyboard shortcuts panel and return to the date input field.',
    ),
    keyboardForwardNavigationInstructions: t(
      'atlas.datePicker.keyboardForwardNavigationInstructions',
      `Navigate forward to interact with the calendar and select a date. Press the question mark key to get the keyboard shortcuts for changing dates.`,
      'Instructions for using the keyboard to navigate forward in the date range picker.',
    ),
    keyboardBackwardNavigationInstructions: t(
      'atlas.datePicker.keyboardBackwardNavigationInstructions',
      `Navigate backward to interact with the calendar and select a date. Press the question mark key to get the keyboard shortcuts for changing dates.`,
      'Instructions for using the keyboard to navigate backwards in the date range picker.',
    ),
  };
  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(
    'startDate',
  );
  const { onCancel } = props;
  const [dateRange, setDateRange] = useState({
    startDate: props.createdAtRange.startDate
      ? props.createdAtRange.startDate.utc()
      : null,
    endDate: props.createdAtRange.endDate
      ? props.createdAtRange.endDate.utc()
      : null,
  } as DateRange);
  const ref = useRef(null);
  const cancelWithEvent = useCallback(() => {
    onCancel();
  }, [onCancel]);

  useOnClickOutside(ref, () => cancelWithEvent());
  useOnEscape(ref, () => cancelWithEvent());
  const firstInteractiveElement = document.querySelector(
    'input[id="startDate"]',
  ) as HTMLElement;
  const lastInteractiveElementRef = useRef<HTMLElement>(null);
  useFocusRestrict(firstInteractiveElement, lastInteractiveElementRef.current!);

  moment.locale(getLanguagePreferenceAsMomentLocale());

  return (
    <StyledDialogAlert
      ref={ref}
      body={
        <StyledDatePicker>
          <DateRangePicker
            startDateId="startDate"
            endDateId="endDate"
            startDatePlaceholderText={t(
              'atlas.datePicker.startDate',
              `Start Date`,
              'The start date in the date range picker selection.',
            )}
            endDatePlaceholderText={t(
              'atlas.datePicker.endDate',
              `End Date`,
              'The end date in the date range picker selection.',
            )}
            onDatesChange={(dates: any) => {
              const currentDateRange = {
                startDate: dates.startDate
                  ? dates.startDate.utc().startOf('day')
                  : null,
                endDate: dates.endDate
                  ? dates.endDate.utc().endOf('day')
                  : null,
              };
              setDateRange(currentDateRange);
            }}
            onFocusChange={(focus: any) => {
              if (!focus) return;
              setFocusedInput(focus);
            }}
            isOutsideRange={(day: moment.Moment) =>
              !isInclusivelyBeforeDay(day, moment()) ||
              !isInclusivelyAfterDay(day, FIRST_ATLAS_DELIVERY)
            }
            focusedInput={focusedInput}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            minDate={FIRST_ATLAS_DELIVERY}
            phrases={datePickerPhrases}
          />
        </StyledDatePicker>
      }
      footer={
        <StyledFooter>
          <CancelButton buttonSize={ButtonPrimary.sm} onClick={cancelWithEvent}>
            {t(
              'atlas.deliveryTracking.cancel',
              `Cancel`,
              'Cancel the current selected date range.',
            )}
          </CancelButton>
          <ButtonPrimary
            ref={lastInteractiveElementRef}
            buttonSize={ButtonPrimary.sm}
            onClick={() => {
              if (dateRange.startDate && dateRange.endDate) {
                props.onDateRangeChange(dateRange);
                props.onCancel();
              }
            }}
          >
            {t(
              'atlas.deliveryTracking.apply',
              `Apply`,
              'Apply the current selected date range.',
            )}
          </ButtonPrimary>
        </StyledFooter>
      }
    />
  );
}
